import React, { useState, useEffect, useCallback, useRef } from 'react';
import { structureAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ScrollArea, ScrollBar } from '../../components/ui/scroll-area';
import { ZoomIn, ZoomOut, Maximize2, User, Users } from 'lucide-react';
import RoleDetailDrawer from './RoleDetailDrawer';

// Styling for Regional Chart
const STYLES = {
  regionHeader: { bg: '#319795', text: '#FFFFFF' },
  directors: { bg: '#319795', text: '#FFFFFF', border: '#2C7A7B' },
  deptHeader: { bg: '#E5E7EB', text: '#374151' },
  manager: { bg: '#4B5563', text: '#FFFFFF' },
  lead: { bg: '#9CA3AF', text: '#1F2937' },
  staff: { bg: '#E5E7EB', text: '#374151' },
  keyMembers: { bg: '#FEF3C7', text: '#92400E', border: '#FCD34D' },
  vacant: { bg: '#FEF3C7', text: '#92400E' }
};

// Role Node Component
const RoleNode = ({ role, style, onClick, size = 'normal' }) => {
  const isVacant = !role.assignedPersonId;
  const width = size === 'small' ? 'w-[140px]' : 'w-[160px]';
  
  return (
    <div
      className={`flex-shrink-0 cursor-pointer transition-all duration-200 hover:scale-105 hover:shadow-lg ${width}`}
      onClick={() => onClick(role)}
      data-testid={`regional-node-${role.id}`}
    >
      <div
        className="rounded-lg shadow-md overflow-hidden"
        style={{ 
          backgroundColor: style.bg,
          border: `2px solid ${style.border || style.bg}`
        }}
      >
        <div className="px-3 py-2 text-center">
          <h3 
            className="font-medium text-xs leading-tight"
            style={{ color: style.text }}
          >
            {role.title}
          </h3>
        </div>
        
        <div 
          className="px-3 py-1.5 text-center"
          style={{ backgroundColor: isVacant ? STYLES.vacant.bg : 'rgba(255,255,255,0.95)' }}
        >
          {isVacant ? (
            <span className="text-xs font-medium" style={{ color: STYLES.vacant.text }}>
              Vacant
            </span>
          ) : (
            <span className="text-xs font-medium" style={{ color: '#374151' }}>
              {role.assignedPerson?.displayName || 'Assigned'}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// Department Column Component
const DepartmentColumn = ({ dept, onClick }) => {
  if (!dept) return null;
  
  const deptStyle = { 
    bg: dept.color || '#4B5563', 
    text: '#FFFFFF',
    border: dept.color || '#4B5563'
  };
  
  return (
    <div className="flex flex-col items-center gap-3 min-w-[160px]">
      {/* Department Header */}
      <div 
        className="px-3 py-2 rounded-lg text-center w-full"
        style={{ backgroundColor: STYLES.deptHeader.bg }}
      >
        <span 
          className="text-xs font-semibold uppercase tracking-wide"
          style={{ color: STYLES.deptHeader.text }}
        >
          {dept.name}
        </span>
      </div>
      
      {/* Manager */}
      {dept.manager && (
        <RoleNode 
          role={dept.manager} 
          style={deptStyle}
          onClick={onClick}
          size="normal"
        />
      )}
      
      {/* Connector line */}
      <div className="w-0.5 h-4" style={{ backgroundColor: '#CBD5E1' }} />
      
      {/* Leads Row */}
      <div className="flex gap-2 justify-center">
        {dept.leads.map((lead) => (
          <RoleNode 
            key={lead.id}
            role={lead} 
            style={STYLES.lead}
            onClick={onClick}
            size="small"
          />
        ))}
      </div>
      
      {/* Connector line */}
      <div className="w-0.5 h-4" style={{ backgroundColor: '#CBD5E1' }} />
      
      {/* Staff Row */}
      <div className="flex flex-col gap-2 items-center">
        {dept.staff.map((staffMember) => (
          <RoleNode 
            key={staffMember.id}
            role={staffMember} 
            style={STYLES.staff}
            onClick={onClick}
            size="small"
          />
        ))}
      </div>
    </div>
  );
};

// Key Members Section
const KeyMembersSection = ({ members, onClick }) => {
  if (!members || members.length === 0) return null;
  
  return (
    <div className="mt-8 pt-6 border-t-2" style={{ borderColor: '#E5E7EB' }}>
      {/* Section Header */}
      <div className="flex justify-center mb-4">
        <div 
          className="px-6 py-2 rounded-full"
          style={{ backgroundColor: STYLES.keyMembers.bg, border: `2px solid ${STYLES.keyMembers.border}` }}
        >
          <span 
            className="text-sm font-semibold uppercase tracking-wide"
            style={{ color: STYLES.keyMembers.text }}
          >
            Key Members
          </span>
        </div>
      </div>
      
      {/* Members List */}
      <div 
        className="max-w-md mx-auto rounded-lg p-4"
        style={{ backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB' }}
      >
        <div className="space-y-2">
          {members.map((member, index) => (
            <div 
              key={member.id}
              className="flex items-center gap-3 p-2 rounded cursor-pointer hover:bg-gray-100"
              onClick={() => onClick(member)}
              data-testid={`key-member-${member.id}`}
            >
              <span 
                className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold"
                style={{ backgroundColor: STYLES.keyMembers.bg, color: STYLES.keyMembers.text }}
              >
                {index + 1}
              </span>
              <span className="flex-1 text-sm" style={{ color: '#374151' }}>
                {member.assignedPerson?.displayName || 'Vacant'}
              </span>
              {!member.assignedPersonId && (
                <Badge 
                  variant="outline" 
                  className="text-xs"
                  style={{ backgroundColor: STYLES.vacant.bg, color: STYLES.vacant.text, borderColor: STYLES.keyMembers.border }}
                >
                  Vacant
                </Badge>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const RegionalChart = ({ departments, people, onUpdate, regionId = 'region_1', regionLabel = 'Region 1' }) => {
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [zoom, setZoom] = useState(0.9);
  const containerRef = useRef(null);

  const fetchRegionalChart = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await structureAPI.getRegionalChart(regionId);
      console.log(`Regional chart response for ${regionId}:`, response.data);
      setChartData(response.data);
    } catch (error) {
      console.error(`Error fetching regional chart for ${regionId}:`, error);
      setError(error.message || 'Failed to load regional chart');
    } finally {
      setLoading(false);
    }
  }, [regionId]);

  useEffect(() => {
    fetchRegionalChart();
  }, [fetchRegionalChart]);

  const handleNodeClick = useCallback((role) => {
    setSelectedRole(role);
    setDrawerOpen(true);
  }, []);

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.1, 1.5));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.1, 0.4));
  const handleResetZoom = () => setZoom(0.9);

  const handleUpdate = async () => {
    await fetchRegionalChart();
    onUpdate?.();
  };

  if (loading) {
    return (
      <div 
        className="flex items-center justify-center h-[800px] rounded-xl"
        style={{ backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB' }}
      >
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin mx-auto mb-3" 
            style={{ borderColor: '#319795', borderTopColor: 'transparent' }} />
          <p style={{ color: '#6B7280' }}>Loading regional chart...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div 
        className="flex flex-col items-center justify-center h-[800px] rounded-xl"
        style={{ backgroundColor: '#FEF2F2', border: '1px solid #FECACA' }}
      >
        <h3 className="text-lg font-medium mb-2" style={{ color: '#991B1B' }}>
          Failed to load chart
        </h3>
        <p className="text-sm mb-4" style={{ color: '#B91C1C' }}>{error}</p>
        <Button onClick={fetchRegionalChart} style={{ backgroundColor: '#DC2626', color: '#FFFFFF' }}>
          Try Again
        </Button>
      </div>
    );
  }

  if (!chartData) {
    return (
      <div 
        className="flex flex-col items-center justify-center h-[800px] rounded-xl"
        style={{ backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB' }}
      >
        <Users className="w-16 h-16 mb-4" style={{ color: '#D1D5DB' }} />
        <h3 className="text-lg font-medium mb-2" style={{ color: '#264653' }}>
          No regional structure yet
        </h3>
      </div>
    );
  }

  return (
    <>
      <div 
        className="rounded-xl overflow-hidden relative"
        style={{ 
          height: '800px', 
          backgroundColor: '#FAFBFC',
          border: '1px solid #E5E7EB'
        }}
        data-testid="regional-chart-container"
      >
        {/* Zoom Controls */}
        <div className="absolute top-4 left-4 z-10 flex flex-col gap-2 bg-white rounded-lg shadow-md p-2">
          <Button variant="ghost" size="sm" onClick={handleZoomIn} className="h-8 w-8 p-0" title="Zoom In">
            <ZoomIn className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={handleZoomOut} className="h-8 w-8 p-0" title="Zoom Out">
            <ZoomOut className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={handleResetZoom} className="h-8 w-8 p-0" title="Reset Zoom">
            <Maximize2 className="w-4 h-4" />
          </Button>
          <div className="text-xs text-center" style={{ color: '#6B7280' }}>
            {Math.round(zoom * 100)}%
          </div>
        </div>

        {/* Legend */}
        <div className="absolute top-4 right-4 z-10 bg-white rounded-lg shadow-md p-3">
          <div className="text-xs font-medium mb-2" style={{ color: '#374151' }}>Legend</div>
          <div className="space-y-1.5">
            {[
              { label: 'Regional Director', style: STYLES.directors },
              { label: 'Manager', style: STYLES.manager },
              { label: 'Lead', style: STYLES.lead },
              { label: 'Staff', style: STYLES.staff },
              { label: 'Key Member', style: STYLES.keyMembers },
            ].map(item => (
              <div key={item.label} className="flex items-center gap-2">
                <div 
                  className="w-3 h-3 rounded"
                  style={{ backgroundColor: item.style.bg, border: `1px solid ${item.style.border || item.style.bg}` }}
                />
                <span className="text-xs" style={{ color: '#6B7280' }}>{item.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Chart Content */}
        <ScrollArea className="h-full w-full">
          <div 
            ref={containerRef}
            className="min-h-full py-6 px-4"
            style={{ 
              transform: `scale(${zoom})`,
              transformOrigin: 'top center',
              minWidth: 'max-content'
            }}
          >
            {/* Region Header */}
            <div className="text-center mb-6">
              <div 
                className="inline-block px-8 py-3 rounded-xl shadow-lg"
                style={{ backgroundColor: STYLES.regionHeader.bg }}
              >
                <h2 className="text-xl font-bold" style={{ color: STYLES.regionHeader.text }}>
                  {chartData.regionName}
                </h2>
              </div>
            </div>

            {/* Connector line */}
            <div className="flex justify-center mb-4">
              <div className="w-0.5 h-6" style={{ backgroundColor: '#CBD5E1' }} />
            </div>

            {/* TIER 1: Regional Directors */}
            <div className="flex justify-center mb-4">
              <div 
                className="px-4 py-1.5 rounded-full"
                style={{ backgroundColor: STYLES.directors.bg }}
              >
                <span className="text-xs font-semibold uppercase" style={{ color: STYLES.directors.text }}>
                  Regional Directors
                </span>
              </div>
            </div>
            
            <div className="flex justify-center gap-4 mb-6">
              {chartData.regionalDirectors.map((director) => (
                <RoleNode 
                  key={director.id}
                  role={director} 
                  style={STYLES.directors}
                  onClick={handleNodeClick}
                />
              ))}
            </div>

            {/* Connector line */}
            <div className="flex justify-center mb-6">
              <div className="w-0.5 h-8" style={{ backgroundColor: '#CBD5E1' }} />
            </div>

            {/* TIER 2: Functional Departments Label */}
            <div className="flex justify-center mb-4">
              <div 
                className="px-4 py-1.5 rounded-full"
                style={{ backgroundColor: '#E5E7EB' }}
              >
                <span className="text-xs font-semibold uppercase" style={{ color: '#374151' }}>
                  Functional Departments
                </span>
              </div>
            </div>

            {/* TIER 2-5: Department Columns */}
            <div className="flex justify-center gap-6">
              {chartData.departmentList.map((dept) => (
                <DepartmentColumn 
                  key={dept.name}
                  dept={dept}
                  onClick={handleNodeClick}
                />
              ))}
            </div>

            {/* Key Members Section */}
            <KeyMembersSection 
              members={chartData.keyMembers}
              onClick={handleNodeClick}
            />
          </div>
          <ScrollBar orientation="horizontal" />
          <ScrollBar orientation="vertical" />
        </ScrollArea>
      </div>

      {/* Role Detail Drawer */}
      <RoleDetailDrawer
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedRole(null);
        }}
        role={selectedRole}
        departments={departments}
        people={people}
        onUpdate={handleUpdate}
      />
    </>
  );
};

export default RegionalChart;
